import { useEffect, useMemo, useRef, useState } from "react";

import { fetchComparableCandidates, submitComparablesOnboarding, type ComparableCandidate } from "@/api/onboarding";
import { Button } from "@/components/ui/button";
import { genreLabel } from "@/constants/genres";
import { Input } from "@/components/ui/input";

type Props = {
  onBack?: () => void;
  onDone: () => Promise<void> | void;
};

const RECOMMENDED_MIN = 3;
const MAX_SELECTED = 10;

const keyOf = (candidate: ComparableCandidate) => `${candidate.kind}:${candidate.id}`;

/**
 * Step 4: who plays the rooms you want to play?
 * Candidates come from stored data only; nothing here looks artists up
 * externally or collects venue history.
 */
export default function ComparablesStep({ onBack, onDone }: Props) {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<"matching" | "all">("matching");
  const [chosenGenres, setChosenGenres] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<ComparableCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, ComparableCandidate>>(new Map());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setCandidates([]);
    const timer = window.setTimeout(
      () => {
        setLoading(true);
        setLoadError(null);
        fetchComparableCandidates(query, scope)
          .then((payload) => {
            if (requestRef.current === requestId) {
              setCandidates(payload.candidates ?? []);
              setChosenGenres(payload.chosen_genres ?? []);
            }
          })
          .catch(() => {
            if (requestRef.current === requestId) setLoadError("We couldn't load artists right now.");
          })
          .finally(() => {
            if (requestRef.current === requestId) setLoading(false);
          });
      },
      query ? 250 : 0,
    );
    return () => { window.clearTimeout(timer); ++requestRef.current; };
  }, [query, scope]);

  const selectedList = useMemo(() => Array.from(selected.values()), [selected]);
  const count = selectedList.length;

  const toggle = (candidate: ComparableCandidate) =>
    setSelected((prev) => {
      const next = new Map(prev);
      const key = keyOf(candidate);
      if (next.has(key)) next.delete(key);
      else if (next.size < MAX_SELECTED) next.set(key, candidate);
      return next;
    });

  const finish = async (skip: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await submitComparablesOnboarding(
        skip
          ? { skip: true }
          : {
              public_artist_ids: selectedList.filter((c) => c.kind === "public").map((c) => c.id),
              resolved_artist_ids: selectedList.filter((c) => c.kind === "resolved").map((c) => c.id),
            },
      );
      await onDone();
    } catch {
      setError("We couldn't save your choices. Please try again.");
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4" aria-label="Comparable artists">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Who plays the kinds of rooms you want to play?</h2>
        <p className="text-sm text-muted-foreground">
          Choose {RECOMMENDED_MIN}–{MAX_SELECTED} artists at or a step above your level. We'll show you where they've
          actually played.
        </p>
      </div>

      <div className="flex gap-2" aria-label="Artist suggestions">
        <Button type="button" variant={scope === "matching" ? "default" : "outline"} aria-pressed={scope === "matching"} onClick={() => setScope("matching")}>Suggested for you</Button>
        <Button type="button" variant={scope === "all" ? "default" : "outline"} aria-pressed={scope === "all"} onClick={() => setScope("all")}>Browse all</Button>
      </div>
      {!loading && <p className="text-sm text-muted-foreground">
        {scope === "all" ? "Browsing all available artists." : chosenGenres.length ? `Matching your genres: ${chosenGenres.map(genreLabel).join(", ")}.` : "Choose genres in your artist profile for tailored suggestions. Showing all available artists."}
      </p>}
      <Input
        placeholder="Search artists"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search artists"
      />

      <div className="max-h-80 space-y-2 overflow-y-auto" role="list" aria-label="Artists you can choose">
        {loading && <p className="text-sm text-muted-foreground">Loading artists…</p>}
        {loadError && <p className="text-sm text-destructive">{loadError}</p>}
        {!loading && !loadError && candidates.length === 0 && (
          <p className="rounded-lg border border-border bg-background/40 p-3 text-sm text-muted-foreground">
            {scope === "matching" && chosenGenres.length ? "No reviewed artists match these genres and search yet. Try Browse all or change your search." : "No artists match your search yet."}
          </p>
        )}
        {candidates.map((candidate) => {
          const active = selected.has(keyOf(candidate));
          return (
            <div key={keyOf(candidate)} role="listitem">
            <button
              type="button"
              onClick={() => toggle(candidate)}
              aria-pressed={active}
              aria-label={`${active ? "Remove" : "Choose"} ${candidate.name}`}
              className={`flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition ${
                active ? "border-primary bg-primary/10" : "border-border bg-background/40 hover:border-primary/40"
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium text-foreground">{candidate.name}</span>
                {(candidate.genre || candidate.has_venue_history) && (
                  <span className="block text-xs text-muted-foreground">
                    {candidate.matched_genres?.length ? `Matches ${candidate.matched_genres.map(genreLabel).join(", ")}` : candidate.genre}
                    {candidate.has_venue_history ? `${candidate.genre || candidate.matched_genres?.length ? " · " : ""}venue history available` : ""}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-xs font-semibold text-primary">{active ? "Chosen" : "Choose"}</span>
            </button>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        Can't find an artist? You can add more later in Discover.
      </p>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {onBack && (
        <div>
          <Button type="button" variant="ghost" onClick={onBack} disabled={saving}>
            Back
          </Button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => void finish(true)}
          disabled={saving}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Skip for now — Discover works best once you've chosen artists
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="comparable-count">
            {count} chosen{count > 0 && count < RECOMMENDED_MIN ? ` · ${RECOMMENDED_MIN} or more recommended` : ""}
          </span>
          <Button onClick={() => void finish(false)} disabled={saving || count === 0}>
            {saving ? "Opening Discover…" : "Explore venues"}
          </Button>
        </div>
      </div>
    </section>
  );
}
